import {
  AccountInfo,
  AuthenticationResult,
  EventType,
  PublicClientApplication,
  SilentRequest,
} from '@azure/msal-browser'

// ---------------------------------------------------------------------------
// Centralised debug helpers
// ---------------------------------------------------------------------------

import { __DEV__, getLogger } from '../utils/logger'

const { debug, error: debugError } = getLogger('TokenService')

/**
 * Centralized MSAL Token Service
 *
 * Provides promise-locked token acquisition to prevent race conditions
 * and handles automatic account state management via MSAL events.
 *
 * Key Features:
 * - Single in-flight promise to prevent duplicate token requests
 * - Automatic account updates via MSAL event callbacks
 * - Graceful error handling for interaction required scenarios
 * - Built-in logging for debugging token acquisition flows
 * - Performance instrumentation for monitoring token acquisition patterns
 */

class TokenService {
  // Performance monitoring
  private callCount = 0

  private sessionStart = Date.now()

  private callHistory: Array<{ timestamp: number; scopes: string[]; source?: string }> = []

  private readonly msalInstance: PublicClientApplication

  private pending?: Promise<string>

  private account: AccountInfo | null = null

  constructor(msalInstance: PublicClientApplication) {
    this.msalInstance = msalInstance
    this.account = msalInstance.getActiveAccount()

    // Subscribe to MSAL events for automatic account management
    this.msalInstance.addEventCallback((message) => {
      debug('MSAL Event:', message.eventType)

      if (
        message.eventType === EventType.LOGIN_SUCCESS ||
        message.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
      ) {
        const result = message.payload as AuthenticationResult
        this.account = result.account
        this.msalInstance.setActiveAccount(result.account)
        debug('Account updated:', result.account?.username)

        /*
         * GrowthBook (feature flag service) expects a bearer that the back-end
         * can validate against its Azure AD app registration.  That means we
         * have to persist the *access token*, not the ID-token.  Using the
         * ID-token led to an hourly 401/CORS storm once the token reached its
         * 60-minute expiry.  The access token is refreshed silently by MSAL
         * whenever <5 minutes remain, therefore GrowthBook will always receive
         * a valid bearer and the 401 pattern disappears.
         */
        if (result.accessToken) {
          localStorage.setItem('auth-token', result.accessToken)
        }
      } else if (message.eventType === EventType.LOGOUT_SUCCESS) {
        this.account = null
        // Clear localStorage token for GrowthBook
        localStorage.removeItem('auth-token')
        // Clear NPS survey timestamps on logout
        localStorage.removeItem('nps_login_timestamp')
        localStorage.removeItem('nps_dismiss_timestamp')
        debug('Account cleared after logout')
      }
    })
  }

  /**
   * Acquire token silently with race condition protection
   *
   * @param scopes - Array of scopes to request
   * @returns Promise<string> - ID token, empty string if no account
   * @throws Error if acquisition fails (but not if no account)
   */
  getToken = async (scopes: string[]): Promise<string> => {
    // Performance instrumentation
    if (__DEV__) {
      this.callCount++
      const callNumber = this.callCount
      const elapsed = Date.now() - this.sessionStart
      const source = this.getCallerSource()

      this.callHistory.push({
        timestamp: Date.now(),
        scopes: [...scopes],
        source,
      })

      // Log every call for first 10, then every 100th call to avoid console spam
      if (callNumber <= 10 || callNumber % 100 === 0) {
        debug(`Call #${callNumber} at +${elapsed}ms from: ${source}`)
        debug('Starting token acquisition for scopes:', scopes)
      }
    }

    if (!this.account) {
      debug('No active account, returning empty token')
      return ''
    }

    // Return existing promise if token acquisition already in progress
    if (this.pending) {
      if (__DEV__) {
        debug(`Token acquisition in progress, sharing promise (call #${this.callCount})`)
      }
      return this.pending
    }

    const request: SilentRequest = {
      scopes,
      account: this.account,
    }

    this.pending = this.msalInstance
      .acquireTokenSilent(request)
      .then((result: AuthenticationResult) => {
        debug('Token acquired successfully')

        /*
         * IMPORTANT
         * ---------
         * For API calls we must send the *access token* (contains the scp / roles
         * that the back-end validates).  The previous implementation returned
         * `idToken`, which is intended only for the front-end and is usually
         * rejected by protected APIs.
         */

        return result.accessToken
      })
      .catch(async (error) => {
        debugError('Silent token acquisition failed:', error)

        if (error.name === 'InteractionRequiredAuthError') {
          debug('Interaction required, redirecting to login')
          // Silent acquisition failed, need user interaction
          await this.msalInstance.loginRedirect({ scopes })
          throw new Error('Redirecting to login - interaction required')
        }

        // Re-throw other errors
        throw error
      })
      .finally(() => {
        debug('Token acquisition promise completed')
        this.pending = undefined
      })

    return this.pending
  }

  /**
   * Get current active account
   * @returns AccountInfo | null
   */
  getAccount = (): AccountInfo | null => this.account

  /**
   * Check if user is authenticated
   * @returns boolean
   */
  isAuthenticated = (): boolean => this.account !== null

  /**
   * Clear token cache and account state
   * Used for testing and logout scenarios
   */
  clearCache = (): void => {
    debug('Clearing cache and account state')
    this.pending = undefined
    this.account = null

    // Clear MSAL instance cache as well
    try {
      this.msalInstance.clearCache()
      debug('MSAL instance cache cleared')
    } catch (error) {
      debugError('Failed to clear MSAL cache:', error)
    }
  }

  /**
   * Get user claims from current account
   * @returns User claims object or null
   */
  getUserClaims = () => {
    if (!this.account?.idTokenClaims) {
      return null
    }

    return {
      email: this.account.idTokenClaims.preferred_username || '',
      name: this.account.idTokenClaims.name || '',
      firstName: this.account.idTokenClaims.firstName || '',
      lastName: this.account.idTokenClaims.lastName || '',
      gpId: this.account.idTokenClaims.gpId || '',
      roles: this.account.idTokenClaims.roles || [],
    }
  }

  /**
   * Get calling function/component for debugging
   * @returns String identifying the caller
   */
  private readonly getCallerSource = (): string => {
    try {
      const stack = new Error().stack || ''
      const lines = stack.split('\n')

      // Log full stack for debugging the "unknown" calls
      if (this.callCount % 100 === 0) {
        debug('Full stack trace for debugging:')
        lines.forEach((line, idx) => {
          debug(`  ${idx}: ${line}`)
        })
      }

      // Find React component or hook calls
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i]

        // Skip TokenService internals
        if (line.includes('TokenService') || line.includes('getToken')) continue

        // Look for React components, hooks, or our code
        if (
          line.includes('src/') ||
          line.includes('useApi') ||
          line.includes('apiFetch') ||
          line.includes('AuthProvider') ||
          line.includes('AuthGuard') ||
          line.includes('useAuth') ||
          line.includes('useMsal') ||
          line.includes('useIsAuthenticated') ||
          line.includes('React') ||
          line.includes('Component')
        ) {
          // Extract detailed location info
          const fileMatch = line.match(/\((.*?):(\d+):(\d+)\)/)
          const funcMatch = line.match(/at\s+([^(]+)/)

          if (fileMatch && funcMatch) {
            const [, filePath, lineNum, colNum] = fileMatch
            const fileName = filePath.split('/').pop() || 'unknown-file'
            const funcName = funcMatch[1]?.trim() || 'anonymous'
            return `${funcName} (${fileName}:${lineNum}:${colNum})`
          }

          // Fallback to simpler parsing
          const match = line.match(/at\s+([^(]+)\s*\((.*?)\)/)
          if (match) {
            const caller = match[1]?.trim() || 'unknown-func'
            const location = match[2]?.trim() || 'unknown-location'
            const fileName = location.split('/').pop()?.split(':')[0] || 'unknown-file'
            return `${caller} (${fileName})`
          }
        }
      }

      // If we get here, log the first few non-TokenService lines
      const relevantLines = lines
        .filter((line) => !line.includes('TokenService') && !line.includes('getToken'))
        .slice(0, 3)
        .map((line) => line.trim())
        .join(' → ')

      return `trace: ${relevantLines.substring(0, 100)}`
    } catch (error) {
      return `error-parsing-stack: ${error.message}`
    }
  }

  /**
   * Get performance statistics for debugging
   * @returns Performance stats object
   */
  getPerformanceStats = () => {
    const sessionDuration = Date.now() - this.sessionStart
    const callsPerMinute = (this.callCount / sessionDuration) * 60000

    // Group calls by source
    const callsBySource = this.callHistory.reduce(
      (acc, call) => {
        const source = call.source || 'unknown'
        acc[source] = (acc[source] || 0) + 1
        return acc
      },
      {} as Record<string, number>
    )

    return {
      totalCalls: this.callCount,
      sessionDurationMs: sessionDuration,
      callsPerMinute: Math.round(callsPerMinute * 100) / 100,
      callsBySource,
      recentCalls: this.callHistory.slice(-10), // Last 10 calls
    }
  }

  /**
   * Reset performance counters (for testing)
   */
  resetPerformanceCounters = () => {
    this.callCount = 0
    this.sessionStart = Date.now()
    this.callHistory = []
    debug('Performance counters reset')
  }
}

export default TokenService
